import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, AutoAwesome as AutoAwesomeIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import protocolRegistrationService from '../../services/protocolRegistration.service';

const emptyPerson = () => ({
  name: '',
  designation: '',
  affiliation: '',
  address: '',
  email: '',
  phone: '',
  fax: '',
});

const initialForm = {
  trial_identification: {
    public_title: '',
    scientific_title: '',
    acronym: '',
    secondary_ids: [{ id_type: '', id_value: '' }],
  },
  investigators_and_contacts: {
    principal_investigator: emptyPerson(),
    contact_scientific_query: emptyPerson(),
    contact_public_query: emptyPerson(),
  },
  sponsorship_and_support: {
    primary_sponsor: { name: '', address: '' },
    secondary_sponsors: [{ name: '', address: '' }],
    source_of_support: [{ support_type: '', organization: '' }],
  },
  regulatory_and_ethics: {
    countries_of_recruitment: [''],
    study_sites: [{ site_name: '', address: '', contact_person: '', email: '', phone: '' }],
    ethics_committees: [{ committee_name: '', city: '', approval_status: '', approval_date: '' }],
    dcgi_approval_status: '',
  },
  study_details: {
    health_condition: {
      participant_type: '',
      icd10_code: '',
    },
    study_type: {
      post_graduation_thesis: false,
      trial_category: '',
      study_design: '',
    },
  },
  intervention_details: {
    interventions: [{ name: '', dose: '', duration: '', route_of_administration: '' }],
    comparator: '',
    randomization_method: '',
    allocation_concealment: '',
    blinding: '',
  },
  eligibility: {
    inclusion_criteria: '',
    exclusion_criteria: '',
    age_min: '',
    age_max: '',
    sex: '',
  },
  outcomes: {
    primary_outcomes: [{ outcome_name: '', timepoint: '' }],
    secondary_outcomes: [{ outcome_name: '', timepoint: '' }],
  },
  sample_size_and_phase: {
    target_sample_size: {
      india: 0,
      total: 0,
    },
    phase_of_trial: '',
  },
  timeline_and_status: {
    date_of_first_enrollment: {
      india: '',
      global: '',
    },
    estimated_trial_duration: '',
    recruitment_status: {
      india: '',
      global: '',
    },
    study_completion_date: {
      india: '',
      global: '',
    },
    final_enrollment_numbers: {
      india: 0,
      total: 0,
    },
  },
  brief_summary: '',
  publication: '',
  ipd_sharing: {
    plan: '',
    description: '',
    when_available: '',
    access_criteria: '',
    data_location: '',
  },
};

const IndiaRegistry = ({ projectId }) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const flattenForPdf = () => {
    const lines = [];
    const push = (label, value) => {
      if (value === undefined || value === null) return;
      const v = String(value).trim();
      if (v) lines.push(`${label}: ${v}`);
    };
    const section = (title) => lines.push('', `--- ${title} ---`, '');
    const fd = formData;
    section('1. Trial Identification');
    push('Public Title', fd.trial_identification?.public_title);
    push('Scientific Title', fd.trial_identification?.scientific_title);
    push('Acronym', fd.trial_identification?.acronym);
    (fd.trial_identification?.secondary_ids || []).forEach((s, i) => {
      if (s?.id_type || s?.id_value) push(`Secondary ID ${i + 1}`, [s.id_type, s.id_value].filter(Boolean).join(' - '));
    });
    section('2. Investigators & Contacts');
    const pi = fd.investigators_and_contacts?.principal_investigator;
    if (pi) {
      push('Principal Investigator', [pi.name, pi.designation, pi.affiliation].filter(Boolean).join(', '));
      push('PI Address', pi.address);
      push('PI Email', pi.email);
      push('PI Phone', pi.phone);
    }
    const sci = fd.investigators_and_contacts?.contact_scientific_query;
    if (sci) push('Contact (Scientific)', [sci.name, sci.email].filter(Boolean).join(', '));
    const pub = fd.investigators_and_contacts?.contact_public_query;
    if (pub) push('Contact (Public)', [pub.name, pub.email].filter(Boolean).join(', '));
    section('3. Sponsorship & Support');
    push('Primary Sponsor', fd.sponsorship_and_support?.primary_sponsor?.name);
    push('Primary Sponsor Address', fd.sponsorship_and_support?.primary_sponsor?.address);
    (fd.sponsorship_and_support?.secondary_sponsors || []).forEach((s, i) => {
      if (s?.name) push(`Secondary Sponsor ${i + 1}`, s.name);
    });
    (fd.sponsorship_and_support?.source_of_support || []).forEach((s, i) => {
      if (s?.support_type || s?.organization) push(`Source of Support ${i + 1}`, [s.support_type, s.organization].filter(Boolean).join(' - '));
    });
    section('4. Regulatory & Ethics');
    push('DCGI Approval Status', fd.regulatory_and_ethics?.dcgi_approval_status);
    (fd.regulatory_and_ethics?.countries_of_recruitment || []).filter(Boolean).forEach((c, i) => push(`Country ${i + 1}`, c));
    (fd.regulatory_and_ethics?.study_sites || []).forEach((s, i) => {
      if (s?.site_name) push(`Study Site ${i + 1}`, [s.site_name, s.address].filter(Boolean).join(', '));
    });
    (fd.regulatory_and_ethics?.ethics_committees || []).forEach((ec, i) => {
      if (ec?.committee_name) push(`Ethics Committee ${i + 1}`, [ec.committee_name, ec.city, ec.approval_status].filter(Boolean).join(', '));
    });
    section('5. Study Details');
    push('Participant Type', fd.study_details?.health_condition?.participant_type);
    push('ICD-10 Code', fd.study_details?.health_condition?.icd10_code);
    push('Post-graduate thesis', fd.study_details?.study_type?.post_graduation_thesis ? 'Yes' : '');
    push('Trial Category', fd.study_details?.study_type?.trial_category);
    push('Study Design', fd.study_details?.study_type?.study_design);
    section('6. Intervention Details');
    (fd.intervention_details?.interventions || []).forEach((intv, i) => {
      if (intv?.name) push(`Intervention ${i + 1}`, [intv.name, intv.dose, intv.duration, intv.route_of_administration].filter(Boolean).join(', '));
    });
    push('Comparator', fd.intervention_details?.comparator);
    push('Randomization', fd.intervention_details?.randomization_method);
    push('Blinding', fd.intervention_details?.blinding);
    section('Eligibility (Inclusion/Exclusion, Age, Sex)');
    push('Inclusion Criteria', fd.eligibility?.inclusion_criteria);
    push('Exclusion Criteria', fd.eligibility?.exclusion_criteria);
    push('Minimum Age', fd.eligibility?.age_min);
    push('Maximum Age', fd.eligibility?.age_max);
    push('Sex', fd.eligibility?.sex);
    section('7. Outcomes');
    (fd.outcomes?.primary_outcomes || []).forEach((o, i) => {
      if (o?.outcome_name) push(`Primary Outcome ${i + 1}`, [o.outcome_name, o.timepoint].filter(Boolean).join(' - '));
    });
    (fd.outcomes?.secondary_outcomes || []).forEach((o, i) => {
      if (o?.outcome_name) push(`Secondary Outcome ${i + 1}`, [o.outcome_name, o.timepoint].filter(Boolean).join(' - '));
    });
    section('8. Sample Size & Timeline');
    push('Target Sample Size (India)', fd.sample_size_and_phase?.target_sample_size?.india);
    push('Target Sample Size (Total)', fd.sample_size_and_phase?.target_sample_size?.total);
    push('Phase of Trial', fd.sample_size_and_phase?.phase_of_trial);
    push('Estimated Duration', fd.timeline_and_status?.estimated_trial_duration);
    push('First Enrollment (India)', fd.timeline_and_status?.date_of_first_enrollment?.india);
    push('First Enrollment (Global)', fd.timeline_and_status?.date_of_first_enrollment?.global);
    push('Recruitment Status (India)', fd.timeline_and_status?.recruitment_status?.india);
    push('Recruitment Status (Global)', fd.timeline_and_status?.recruitment_status?.global);
    push('Study Completion (India)', fd.timeline_and_status?.study_completion_date?.india);
    push('Study Completion (Global)', fd.timeline_and_status?.study_completion_date?.global);
    push('Final Enrollment (India)', fd.timeline_and_status?.final_enrollment_numbers?.india);
    push('Final Enrollment (Total)', fd.timeline_and_status?.final_enrollment_numbers?.total);
    section('9. Brief Summary');
    push('Brief Summary', fd.brief_summary);
    section('10. Publication');
    push('Publication', fd.publication);
    section('11. IPD Sharing Statement');
    push('Plan to Share IPD', fd.ipd_sharing?.plan);
    push('IPD Description', fd.ipd_sharing?.description);
    push('When Available', fd.ipd_sharing?.when_available);
    push('Access Criteria', fd.ipd_sharing?.access_criteria);
    push('Data Location', fd.ipd_sharing?.data_location);
    return lines.join('\n');
  };

  const handleExportPdf = async () => {
    try {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxWidth = pageWidth - margin * 2;
      const lineHeight = 6;
      let y = margin;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('India Clinical Trials Registry (CTRI) – Dataset', margin, y);
      y += 12;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      const text = flattenForPdf();
      const lines = doc.splitTextToSize(text, maxWidth);
      for (let i = 0; i < lines.length; i++) {
        if (y > doc.internal.pageSize.getHeight() - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(lines[i], margin, y);
        y += lineHeight;
      }
      doc.save(`India-CTRI-Registry-${projectId || 'export'}.pdf`);
      showSnackbar('PDF exported successfully', 'success');
    } catch (err) {
      console.error('PDF export failed:', err);
      showSnackbar(err?.message || 'Failed to export PDF', 'error');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const regData = await protocolRegistrationService.getByProject(projectId);
        const india = regData?.india_registry || {};
        const merged = { ...initialForm, ...india };

        // Normalize arrays and nested objects to avoid undefined
        merged.trial_identification = {
          ...initialForm.trial_identification,
          ...(india.trial_identification || {}),
        };
        if (!merged.trial_identification.secondary_ids || merged.trial_identification.secondary_ids.length === 0) {
          merged.trial_identification.secondary_ids = [{ id_type: '', id_value: '' }];
        }

        merged.investigators_and_contacts = {
          ...initialForm.investigators_and_contacts,
          ...(india.investigators_and_contacts || {}),
        };
        merged.investigators_and_contacts.principal_investigator = {
          ...emptyPerson(),
          ...(india.investigators_and_contacts?.principal_investigator || {}),
        };
        merged.investigators_and_contacts.contact_scientific_query = {
          ...emptyPerson(),
          ...(india.investigators_and_contacts?.contact_scientific_query || {}),
        };
        merged.investigators_and_contacts.contact_public_query = {
          ...emptyPerson(),
          ...(india.investigators_and_contacts?.contact_public_query || {}),
        };

        merged.sponsorship_and_support = {
          ...initialForm.sponsorship_and_support,
          ...(india.sponsorship_and_support || {}),
        };
        if (!merged.sponsorship_and_support.secondary_sponsors || merged.sponsorship_and_support.secondary_sponsors.length === 0) {
          merged.sponsorship_and_support.secondary_sponsors = [{ name: '', address: '' }];
        }
        if (!merged.sponsorship_and_support.source_of_support || merged.sponsorship_and_support.source_of_support.length === 0) {
          merged.sponsorship_and_support.source_of_support = [{ support_type: '', organization: '' }];
        }

        merged.regulatory_and_ethics = {
          ...initialForm.regulatory_and_ethics,
          ...(india.regulatory_and_ethics || {}),
        };
        if (!merged.regulatory_and_ethics.countries_of_recruitment || merged.regulatory_and_ethics.countries_of_recruitment.length === 0) {
          merged.regulatory_and_ethics.countries_of_recruitment = [''];
        }
        if (!merged.regulatory_and_ethics.study_sites || merged.regulatory_and_ethics.study_sites.length === 0) {
          merged.regulatory_and_ethics.study_sites = [{ site_name: '', address: '', contact_person: '', email: '', phone: '' }];
        }
        if (!merged.regulatory_and_ethics.ethics_committees || merged.regulatory_and_ethics.ethics_committees.length === 0) {
          merged.regulatory_and_ethics.ethics_committees = [{ committee_name: '', city: '', approval_status: '', approval_date: '' }];
        }

        merged.study_details = {
          ...initialForm.study_details,
          ...(india.study_details || {}),
        };

        merged.intervention_details = {
          ...initialForm.intervention_details,
          ...(india.intervention_details || {}),
        };
        if (!merged.intervention_details.interventions || merged.intervention_details.interventions.length === 0) {
          merged.intervention_details.interventions = [{ name: '', dose: '', duration: '', route_of_administration: '' }];
        }

        merged.eligibility = {
          ...initialForm.eligibility,
          ...(india.eligibility || {}),
        };

        merged.outcomes = {
          ...initialForm.outcomes,
          ...(india.outcomes || {}),
        };
        if (!merged.outcomes.primary_outcomes || merged.outcomes.primary_outcomes.length === 0) {
          merged.outcomes.primary_outcomes = [{ outcome_name: '', timepoint: '' }];
        }
        if (!merged.outcomes.secondary_outcomes || merged.outcomes.secondary_outcomes.length === 0) {
          merged.outcomes.secondary_outcomes = [{ outcome_name: '', timepoint: '' }];
        }

        merged.sample_size_and_phase = {
          ...initialForm.sample_size_and_phase,
          ...(india.sample_size_and_phase || {}),
        };
        merged.timeline_and_status = {
          ...initialForm.timeline_and_status,
          ...(india.timeline_and_status || {}),
        };

        // Convert possible Date objects to yyyy-mm-dd strings for date fields
        const toDateInput = (value) =>
          value ? new Date(value).toISOString().split('T')[0] : '';

        merged.timeline_and_status.date_of_first_enrollment = {
          ...initialForm.timeline_and_status.date_of_first_enrollment,
          ...(india.timeline_and_status?.date_of_first_enrollment || {}),
        };
        merged.timeline_and_status.date_of_first_enrollment.india = toDateInput(
          merged.timeline_and_status.date_of_first_enrollment.india
        );
        merged.timeline_and_status.date_of_first_enrollment.global = toDateInput(
          merged.timeline_and_status.date_of_first_enrollment.global
        );

        merged.timeline_and_status.study_completion_date = {
          ...initialForm.timeline_and_status.study_completion_date,
          ...(india.timeline_and_status?.study_completion_date || {}),
        };
        merged.timeline_and_status.study_completion_date.india = toDateInput(
          merged.timeline_and_status.study_completion_date.india
        );
        merged.timeline_and_status.study_completion_date.global = toDateInput(
          merged.timeline_and_status.study_completion_date.global
        );

        merged.brief_summary = india.brief_summary != null ? india.brief_summary : initialForm.brief_summary;
        merged.publication = india.publication != null ? india.publication : initialForm.publication;
        merged.ipd_sharing = {
          ...initialForm.ipd_sharing,
          ...(india.ipd_sharing || {}),
        };

        setFormData(merged);
      } catch (error) {
        console.error('Error loading India registry:', error);
        showSnackbar('Failed to load India registry data', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  const handleArrayChange = (path, index, field, value) => {
    setFormData((prev) => {
      const segments = path.split('.');
      const updated = { ...prev };
      let current = updated;

      for (let i = 0; i < segments.length - 1; i++) {
        const key = segments[i];
        current[key] = Array.isArray(current[key])
          ? [...current[key]]
          : { ...(current[key] || {}) };
        current = current[key];
      }

      const lastKey = segments[segments.length - 1];
      const arr = Array.isArray(current[lastKey]) ? [...current[lastKey]] : [];

      if (typeof arr[index] === 'object' && arr[index] !== null && field) {
        arr[index] = { ...arr[index], [field]: value };
      } else {
        arr[index] = value;
      }

      current[lastKey] = arr;
      return updated;
    });
  };

  const handleAddArrayItem = (path, defaultItem) => {
    setFormData((prev) => {
      const segments = path.split('.');
      const updated = { ...prev };
      let current = updated;
      for (let i = 0; i < segments.length; i++) {
        if (!Array.isArray(current[segments[i]])) {
          current[segments[i]] = [];
        } else {
          current[segments[i]] = [...current[segments[i]]];
        }
        if (i === segments.length - 1) {
          current[segments[i]].push(defaultItem);
        } else {
          current = current[segments[i]];
        }
      }
      return updated;
    });
  };

  const handleRemoveArrayItem = (path, index) => {
    setFormData((prev) => {
      const segments = path.split('.');
      const updated = { ...prev };
      let current = updated;
      for (let i = 0; i < segments.length; i++) {
        current[segments[i]] = [...(current[segments[i]] || [])];
        if (i === segments.length - 1) {
          if (current[segments[i]].length > 1) {
            current[segments[i]].splice(index, 1);
          }
        } else {
          current = current[segments[i]];
        }
      }
      return updated;
    });
  };

  const handleNestedChange = (path, field, value) => {
    setFormData((prev) => {
      const segments = path.split('.');
      const updated = { ...prev };
      let current = updated;
      for (let i = 0; i < segments.length; i++) {
        current[segments[i]] = { ...(current[segments[i]] || {}) };
        if (i === segments.length - 1) {
          current[segments[i]][field] = value;
        } else {
          current = current[segments[i]];
        }
      }
      return updated;
    });
  };

  const handleTimelineNestedChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      timeline_and_status: {
        ...prev.timeline_and_status,
        [section]: {
          ...prev.timeline_and_status[section],
          [field]: value,
        },
      },
    }));
  };

  const normalizePayload = (data) => ({
    india_registry: data,
  });

  const handleSave = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }
    setSaving(true);
    try {
      await protocolRegistrationService.upsertByProject(projectId, normalizePayload(formData));
      showSnackbar('India (CTRI) registry saved successfully', 'success');
    } catch (error) {
      console.error('Error saving India registry:', error);
      showSnackbar(error?.message || 'Failed to save India registry', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoPopulateFromCoreDatasheet = async () => {
    if (!projectId) {
      showSnackbar('Project ID is required', 'error');
      return;
    }
    setAutoPopulating(true);
    try {
      const response = await protocolRegistrationService.autoPopulateRegistry(projectId, 'india');
      if (response.success && response.data) {
        // Reload from backend to sync with saved india_registry
        const regData = await protocolRegistrationService.getByProject(projectId);
        const india = regData?.india_registry || {};
        setFormData((prev) => ({ ...prev, ...india }));
        showSnackbar('India (CTRI) registry auto-populated from Core Datasheet. Review and save if needed.', 'success');
      } else {
        showSnackbar(response.message || 'Failed to auto-populate India (CTRI) registry', 'error');
      }
    } catch (error) {
      console.error('Error auto-populating India registry:', error);
      showSnackbar(
        error.response?.data?.message || 'Failed to auto-populate India (CTRI) registry',
        'error'
      );
    } finally {
      setAutoPopulating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, px: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            India Clinical Trials Registry (CTRI)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage the India-specific CTRI registration fields. Core fields can be auto-populated from the
            Canonical Datasheet; please review and complete CTRI-only fields manually.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={autoPopulating ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={handleAutoPopulateFromCoreDatasheet}
            disabled={autoPopulating || !projectId}
          >
            {autoPopulating ? 'Auto-Populating...' : 'Auto-Populate from Core Datasheet'}
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !projectId}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PdfIcon />}
            onClick={handleExportPdf}
          >
            Export as PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Trial Identification */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              1. Trial Identification
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Public Title"
                  value={formData.trial_identification.public_title}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trial_identification: {
                        ...prev.trial_identification,
                        public_title: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Scientific Title"
                  value={formData.trial_identification.scientific_title}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trial_identification: {
                        ...prev.trial_identification,
                        scientific_title: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Acronym"
                  value={formData.trial_identification.acronym}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      trial_identification: {
                        ...prev.trial_identification,
                        acronym: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Secondary IDs (other registries)
                </Typography>
                {formData.trial_identification.secondary_ids.map((sid, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="ID Type"
                        value={sid.id_type}
                        onChange={(e) => handleArrayChange('trial_identification.secondary_ids', index, 'id_type', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="ID Value"
                        value={sid.id_value}
                        onChange={(e) => handleArrayChange('trial_identification.secondary_ids', index, 'id_value', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('trial_identification.secondary_ids', index)}
                        disabled={formData.trial_identification.secondary_ids.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.trial_identification.secondary_ids.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('trial_identification.secondary_ids', { id_type: '', id_value: '' })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Investigators & Contacts */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              2. Investigators & Contacts
            </Typography>
            <Grid container spacing={2}>
              {/* Principal Investigator */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Principal Investigator</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.investigators_and_contacts.principal_investigator.name}
                  onChange={(e) => handleNestedChange('investigators_and_contacts.principal_investigator', 'name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Designation"
                  value={formData.investigators_and_contacts.principal_investigator.designation}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.principal_investigator', 'designation', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Affiliation"
                  value={formData.investigators_and_contacts.principal_investigator.affiliation}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.principal_investigator', 'affiliation', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.investigators_and_contacts.principal_investigator.address}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.principal_investigator', 'address', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={formData.investigators_and_contacts.principal_investigator.email}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.principal_investigator', 'email', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.investigators_and_contacts.principal_investigator.phone}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.principal_investigator', 'phone', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Fax"
                  value={formData.investigators_and_contacts.principal_investigator.fax}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.principal_investigator', 'fax', e.target.value)
                  }
                />
              </Grid>

              {/* Scientific Query Contact */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Contact for Scientific Query</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.investigators_and_contacts.contact_scientific_query.name}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_scientific_query', 'name', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Designation"
                  value={formData.investigators_and_contacts.contact_scientific_query.designation}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_scientific_query', 'designation', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Affiliation"
                  value={formData.investigators_and_contacts.contact_scientific_query.affiliation}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_scientific_query', 'affiliation', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.investigators_and_contacts.contact_scientific_query.address}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_scientific_query', 'address', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={formData.investigators_and_contacts.contact_scientific_query.email}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_scientific_query', 'email', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.investigators_and_contacts.contact_scientific_query.phone}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_scientific_query', 'phone', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Fax"
                  value={formData.investigators_and_contacts.contact_scientific_query.fax}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_scientific_query', 'fax', e.target.value)
                  }
                />
              </Grid>

              {/* Public Query Contact */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Contact for Public Query</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.investigators_and_contacts.contact_public_query.name}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_public_query', 'name', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Designation"
                  value={formData.investigators_and_contacts.contact_public_query.designation}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_public_query', 'designation', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Affiliation"
                  value={formData.investigators_and_contacts.contact_public_query.affiliation}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_public_query', 'affiliation', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.investigators_and_contacts.contact_public_query.address}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_public_query', 'address', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="email"
                  label="Email"
                  value={formData.investigators_and_contacts.contact_public_query.email}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_public_query', 'email', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.investigators_and_contacts.contact_public_query.phone}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_public_query', 'phone', e.target.value)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Fax"
                  value={formData.investigators_and_contacts.contact_public_query.fax}
                  onChange={(e) =>
                    handleNestedChange('investigators_and_contacts.contact_public_query', 'fax', e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Sponsorship & Support */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              3. Sponsorship & Support
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary Sponsor Name"
                  value={formData.sponsorship_and_support.primary_sponsor.name}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sponsorship_and_support: {
                        ...prev.sponsorship_and_support,
                        primary_sponsor: {
                          ...prev.sponsorship_and_support.primary_sponsor,
                          name: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Primary Sponsor Address"
                  value={formData.sponsorship_and_support.primary_sponsor.address}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sponsorship_and_support: {
                        ...prev.sponsorship_and_support,
                        primary_sponsor: {
                          ...prev.sponsorship_and_support.primary_sponsor,
                          address: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Secondary Sponsors
                </Typography>
                {formData.sponsorship_and_support.secondary_sponsors.map((sponsor, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={sponsor.name}
                        onChange={(e) =>
                          handleArrayChange('sponsorship_and_support.secondary_sponsors', index, 'name', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={sponsor.address}
                        onChange={(e) =>
                          handleArrayChange('sponsorship_and_support.secondary_sponsors', index, 'address', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('sponsorship_and_support.secondary_sponsors', index)}
                        disabled={formData.sponsorship_and_support.secondary_sponsors.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.sponsorship_and_support.secondary_sponsors.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('sponsorship_and_support.secondary_sponsors', {
                              name: '',
                              address: '',
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Source of Support
                </Typography>
                {formData.sponsorship_and_support.source_of_support.map((item, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Support Type"
                        value={item.support_type}
                        onChange={(e) =>
                          handleArrayChange('sponsorship_and_support.source_of_support', index, 'support_type', e.target.value)
                        }
                        placeholder="e.g., Grant, Industry"
                      />
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Organization"
                        value={item.organization}
                        onChange={(e) =>
                          handleArrayChange('sponsorship_and_support.source_of_support', index, 'organization', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('sponsorship_and_support.source_of_support', index)}
                        disabled={formData.sponsorship_and_support.source_of_support.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.sponsorship_and_support.source_of_support.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('sponsorship_and_support.source_of_support', {
                              support_type: '',
                              organization: '',
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        {/* Regulatory & Ethics */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              4. Regulatory & Ethics (India)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="DCGI Approval Status"
                  value={formData.regulatory_and_ethics.dcgi_approval_status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      regulatory_and_ethics: {
                        ...prev.regulatory_and_ethics,
                        dcgi_approval_status: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Countries of Recruitment
                </Typography>
                {formData.regulatory_and_ethics.countries_of_recruitment.map((c, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={10}>
                      <TextField
                        fullWidth
                        label={`Country ${index + 1}`}
                        value={c}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.countries_of_recruitment', index, null, e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('regulatory_and_ethics.countries_of_recruitment', index)}
                        disabled={formData.regulatory_and_ethics.countries_of_recruitment.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.regulatory_and_ethics.countries_of_recruitment.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('regulatory_and_ethics.countries_of_recruitment', '')
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Study Sites
                </Typography>
                {formData.regulatory_and_ethics.study_sites.map((site, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Site Name"
                        value={site.site_name}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.study_sites', index, 'site_name', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Address"
                        value={site.address}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.study_sites', index, 'address', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Contact Person"
                        value={site.contact_person}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.study_sites', index, 'contact_person', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        type="email"
                        label="Email"
                        value={site.email}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.study_sites', index, 'email', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={site.phone}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.study_sites', index, 'phone', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('regulatory_and_ethics.study_sites', index)}
                        disabled={formData.regulatory_and_ethics.study_sites.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.regulatory_and_ethics.study_sites.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('regulatory_and_ethics.study_sites', {
                              site_name: '',
                              address: '',
                              contact_person: '',
                              email: '',
                              phone: '',
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Ethics Committees
                </Typography>
                {formData.regulatory_and_ethics.ethics_committees.map((ec, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Committee Name"
                        value={ec.committee_name}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.ethics_committees', index, 'committee_name', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="City"
                        value={ec.city}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.ethics_committees', index, 'city', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Approval Status"
                        value={ec.approval_status}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.ethics_committees', index, 'approval_status', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Approval Date"
                        value={ec.approval_date || ''}
                        onChange={(e) =>
                          handleArrayChange('regulatory_and_ethics.ethics_committees', index, 'approval_date', e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('regulatory_and_ethics.ethics_committees', index)}
                        disabled={formData.regulatory_and_ethics.ethics_committees.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.regulatory_and_ethics.ethics_committees.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('regulatory_and_ethics.ethics_committees', {
                              committee_name: '',
                              city: '',
                              approval_status: '',
                              approval_date: '',
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Study Details (India) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              5. Study Details (India)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Participant Type"
                  value={formData.study_details.health_condition.participant_type}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_details: {
                        ...prev.study_details,
                        health_condition: {
                          ...prev.study_details.health_condition,
                          participant_type: e.target.value,
                        },
                      },
                    }))
                  }
                  placeholder="e.g., Healthy volunteers, Patients"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="ICD-10 Code"
                  value={formData.study_details.health_condition.icd10_code}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_details: {
                        ...prev.study_details,
                        health_condition: {
                          ...prev.study_details.health_condition,
                          icd10_code: e.target.value,
                        },
                      },
                    }))
                  }
                  placeholder="e.g., C50.9"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.study_details.study_type.post_graduation_thesis}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          study_details: {
                            ...prev.study_details,
                            study_type: {
                              ...prev.study_details.study_type,
                              post_graduation_thesis: e.target.checked,
                            },
                          },
                        }))
                      }
                    />
                  }
                  label="Post-graduate thesis trial"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CTRI Trial Category"
                  value={formData.study_details.study_type.trial_category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_details: {
                        ...prev.study_details,
                        study_type: {
                          ...prev.study_details.study_type,
                          trial_category: e.target.value,
                        },
                      },
                    }))
                  }
                  placeholder="e.g., Drug, Device, Surgical"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Study Design (CTRI)"
                  value={formData.study_details.study_type.study_design}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      study_details: {
                        ...prev.study_details,
                        study_type: {
                          ...prev.study_details.study_type,
                          study_design: e.target.value,
                        },
                      },
                    }))
                  }
                  placeholder="Narrative description for CTRI"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Intervention Details (India) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              6. Intervention Details (India)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Interventions
                </Typography>
                {formData.intervention_details.interventions.map((intv, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={intv.name}
                        onChange={(e) =>
                          handleArrayChange('intervention_details.interventions', index, 'name', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Dose"
                        value={intv.dose}
                        onChange={(e) =>
                          handleArrayChange('intervention_details.interventions', index, 'dose', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Duration"
                        value={intv.duration}
                        onChange={(e) =>
                          handleArrayChange('intervention_details.interventions', index, 'duration', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        fullWidth
                        label="Route of Administration"
                        value={intv.route_of_administration}
                        onChange={(e) =>
                          handleArrayChange(
                            'intervention_details.interventions',
                            index,
                            'route_of_administration',
                            e.target.value
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() =>
                          handleRemoveArrayItem('intervention_details.interventions', index)
                        }
                        disabled={formData.intervention_details.interventions.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.intervention_details.interventions.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('intervention_details.interventions', {
                              name: '',
                              dose: '',
                              duration: '',
                              route_of_administration: '',
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Comparator"
                  value={formData.intervention_details.comparator}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      intervention_details: {
                        ...prev.intervention_details,
                        comparator: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Randomization Method"
                  value={formData.intervention_details.randomization_method}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      intervention_details: {
                        ...prev.intervention_details,
                        randomization_method: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Allocation Concealment"
                  value={formData.intervention_details.allocation_concealment}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      intervention_details: {
                        ...prev.intervention_details,
                        allocation_concealment: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Blinding"
                  value={formData.intervention_details.blinding}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      intervention_details: {
                        ...prev.intervention_details,
                        blinding: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Eligibility (Inclusion/Exclusion, Age, Sex) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Eligibility (Inclusion/Exclusion, Age, Sex)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Inclusion and exclusion criteria for participant selection. Age and sex in specific boxes as per CTRI.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Inclusion Criteria"
                  value={formData.eligibility.inclusion_criteria}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        inclusion_criteria: e.target.value,
                      },
                    }))
                  }
                  placeholder="Separate each criterion by new line (Enter)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Exclusion Criteria"
                  value={formData.eligibility.exclusion_criteria}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        exclusion_criteria: e.target.value,
                      },
                    }))
                  }
                  placeholder="Separate each criterion by new line (Enter)"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Minimum Age"
                  value={formData.eligibility.age_min}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        age_min: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g., 18 Years"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Maximum Age"
                  value={formData.eligibility.age_max}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        age_max: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g., 65 Years"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Sex"
                  value={formData.eligibility.sex}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      eligibility: {
                        ...prev.eligibility,
                        sex: e.target.value,
                      },
                    }))
                  }
                  placeholder="All / Male / Female"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Outcomes (India) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              7. Outcomes (India)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Primary Outcomes
                </Typography>
                {formData.outcomes.primary_outcomes.map((o, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Outcome Name"
                        value={o.outcome_name}
                        onChange={(e) =>
                          handleArrayChange('outcomes.primary_outcomes', index, 'outcome_name', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Timepoint"
                        value={o.timepoint}
                        onChange={(e) =>
                          handleArrayChange('outcomes.primary_outcomes', index, 'timepoint', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('outcomes.primary_outcomes', index)}
                        disabled={formData.outcomes.primary_outcomes.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.outcomes.primary_outcomes.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('outcomes.primary_outcomes', {
                              outcome_name: '',
                              timepoint: '',
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Secondary Outcomes
                </Typography>
                {formData.outcomes.secondary_outcomes.map((o, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Outcome Name"
                        value={o.outcome_name}
                        onChange={(e) =>
                          handleArrayChange('outcomes.secondary_outcomes', index, 'outcome_name', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Timepoint"
                        value={o.timepoint}
                        onChange={(e) =>
                          handleArrayChange('outcomes.secondary_outcomes', index, 'timepoint', e.target.value)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <IconButton
                        onClick={() => handleRemoveArrayItem('outcomes.secondary_outcomes', index)}
                        disabled={formData.outcomes.secondary_outcomes.length === 1}
                      >
                        <DeleteIcon />
                      </IconButton>
                      {index === formData.outcomes.secondary_outcomes.length - 1 && (
                        <IconButton
                          onClick={() =>
                            handleAddArrayItem('outcomes.secondary_outcomes', {
                              outcome_name: '',
                              timepoint: '',
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Sample Size & Timeline (key India-specific fields) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              8. Sample Size & Timeline (India)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Target Sample Size (India)"
                  value={formData.sample_size_and_phase.target_sample_size.india}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sample_size_and_phase: {
                        ...prev.sample_size_and_phase,
                        target_sample_size: {
                          ...prev.sample_size_and_phase.target_sample_size,
                          india: Number(e.target.value || 0),
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Target Sample Size (Total)"
                  value={formData.sample_size_and_phase.target_sample_size.total}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sample_size_and_phase: {
                        ...prev.sample_size_and_phase,
                        target_sample_size: {
                          ...prev.sample_size_and_phase.target_sample_size,
                          total: Number(e.target.value || 0),
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Phase of Trial"
                  value={formData.sample_size_and_phase.phase_of_trial}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      sample_size_and_phase: {
                        ...prev.sample_size_and_phase,
                        phase_of_trial: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Estimated Trial Duration"
                  value={formData.timeline_and_status.estimated_trial_duration}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      timeline_and_status: {
                        ...prev.timeline_and_status,
                        estimated_trial_duration: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g., 24 months"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Recruitment Status (India)"
                  value={formData.timeline_and_status.recruitment_status.india}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      timeline_and_status: {
                        ...prev.timeline_and_status,
                        recruitment_status: {
                          ...prev.timeline_and_status.recruitment_status,
                          india: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Recruitment Status (Global)"
                  value={formData.timeline_and_status.recruitment_status.global}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      timeline_and_status: {
                        ...prev.timeline_and_status,
                        recruitment_status: {
                          ...prev.timeline_and_status.recruitment_status,
                          global: e.target.value,
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="First Enrollment (India)"
                  value={formData.timeline_and_status.date_of_first_enrollment.india}
                  onChange={(e) =>
                    handleTimelineNestedChange('date_of_first_enrollment', 'india', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Study Completion Date (India)"
                  value={formData.timeline_and_status.study_completion_date.india}
                  onChange={(e) =>
                    handleTimelineNestedChange('study_completion_date', 'india', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Study Completion Date (Global)"
                  value={formData.timeline_and_status.study_completion_date.global}
                  onChange={(e) =>
                    handleTimelineNestedChange('study_completion_date', 'global', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Final Enrollment (India)"
                  value={formData.timeline_and_status.final_enrollment_numbers.india}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      timeline_and_status: {
                        ...prev.timeline_and_status,
                        final_enrollment_numbers: {
                          ...prev.timeline_and_status.final_enrollment_numbers,
                          india: Number(e.target.value || 0),
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Final Enrollment (Total)"
                  value={formData.timeline_and_status.final_enrollment_numbers.total}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      timeline_and_status: {
                        ...prev.timeline_and_status,
                        final_enrollment_numbers: {
                          ...prev.timeline_and_status.final_enrollment_numbers,
                          total: Number(e.target.value || 0),
                        },
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="First Enrollment (Global)"
                  value={formData.timeline_and_status.date_of_first_enrollment.global}
                  onChange={(e) =>
                    handleTimelineNestedChange('date_of_first_enrollment', 'global', e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* 9. Brief Summary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              9. Brief Summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Short description of the primary purpose of the protocol, including a brief statement of the study hypothesis. Include publication details (link/reference) if any.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Brief Summary"
              value={formData.brief_summary}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, brief_summary: e.target.value }))
              }
              placeholder="e.g., This study is a randomized, double blind..."
            />
          </Paper>
        </Grid>

        {/* 10. Publication */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              10. Publication
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Publications arising directly from this trial. If none yet, mention NIL.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Publication"
              value={formData.publication}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, publication: e.target.value }))
              }
              placeholder="e.g., NIL or citation/link"
            />
          </Paper>
        </Grid>

        {/* 11. IPD Sharing Statement */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              11. Individual Participant Data (IPD) Sharing Statement
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Data sharing plan (ICMJE requirement). Indicate whether IPD will be shared; what data, when, where, how; access conditions; additional documents if any.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Plan to Share IPD"
                  value={formData.ipd_sharing.plan}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        plan: e.target.value,
                      },
                    }))
                  }
                  placeholder="Yes / No"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="IPD Sharing Plan Description"
                  value={formData.ipd_sharing.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        description: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="When Available"
                  value={formData.ipd_sharing.when_available}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        when_available: e.target.value,
                      },
                    }))
                  }
                  placeholder="e.g., After publication"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Access Criteria"
                  value={formData.ipd_sharing.access_criteria}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        access_criteria: e.target.value,
                      },
                    }))
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Data Location / URL"
                  value={formData.ipd_sharing.data_location}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      ipd_sharing: {
                        ...prev.ipd_sharing,
                        data_location: e.target.value,
                      },
                    }))
                  }
                  placeholder="Where data will be available"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IndiaRegistry;

